// apps/web/src/features/crm/leads/activity/components/LeadUpcomingTasksStrip.tsx
//
// Upcoming-tasks ("scheduled") strip on the episode feed (#1809): a compact
// list of this lead's upcoming tasks. Upcoming = not yet completed AND carries
// a due anchor, sorted by `dueAt` ascending. The read endpoint already returns
// only OPEN tasks, so the predicate is mainly a `dueAt != null` filter here.
import { useTranslation } from 'react-i18next';
import { type LeadTask } from '@magimp/shared';
import { useLeadTasks } from '@magimp/shared-hooks';
import { Icon } from '../../../../../ui/index.js';
import { formatDateTime } from '../../../../../i18n/index.js';
import { hasPermission, useAuthStore } from '../../../../../stores/auth.js';

export interface LeadUpcomingTasksStripProps {
  leadId: string;
}

function isUpcoming(task: LeadTask): boolean {
  return task.completedAt === null && task.dueAt !== null;
}

export function LeadUpcomingTasksStrip({ leadId }: LeadUpcomingTasksStripProps) {
  const { t } = useTranslation(['leads']);
  const perms = useAuthStore((s) => s.permissions);
  const canReadTasks = perms === 'ALL' || hasPermission(perms, 'tasks.read');
  const tasksQ = useLeadTasks(leadId, { enabled: canReadTasks });

  if (!canReadTasks) return null;

  const upcoming = (tasksQ.data?.items ?? [])
    .filter(isUpcoming)
    .sort((a, b) => (a.dueAt ?? '').localeCompare(b.dueAt ?? ''));

  return (
    <section
      data-testid="lead-upcoming-tasks"
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 6,
        padding: '10px 12px',
        borderRadius: 'var(--r-2)',
        background: 'var(--bg-2)',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          fontSize: 12,
          fontWeight: 600,
          color: 'var(--fg-2)',
        }}
      >
        <Icon name="calendar" size={14} />
        {t('leads:episode.upcoming.title')}
      </div>
      {upcoming.length === 0 ? (
        <div style={{ fontSize: 12, color: 'var(--fg-3)' }} data-testid="lead-upcoming-tasks-empty">
          {t('leads:episode.upcoming.empty')}
        </div>
      ) : (
        <ul
          style={{
            margin: 0,
            padding: 0,
            listStyle: 'none',
            display: 'flex',
            flexDirection: 'column',
            gap: 4,
          }}
        >
          {upcoming.map((task) => (
            <li
              key={task.id}
              data-testid="lead-upcoming-tasks-row"
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                gap: 8,
                fontSize: 13,
                color: 'var(--fg-1)',
              }}
            >
              <span
                style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                title={task.title}
              >
                {task.title}
              </span>
              {task.dueAt && (
                <time dateTime={task.dueAt} style={{ color: 'var(--fg-3)', flex: 'none' }}>
                  {formatDateTime(task.dueAt)}
                </time>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
